import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronLeft, ChevronRight, Calendar, Clock, MapPin, Plus, Minus,
  Sun, Sunset, Moon, Package, Truck, Store, Sparkles, CheckCircle2, ShieldCheck,
} from 'lucide-react';
import { useBookingStore } from '../../store/bookingStore';
import { useMachines, useRentalPackages, useDeliveryZones, usePickupLocations, useAddOns, useIdentityVerification } from '../../hooks/useAPI';

interface BookingScreenProps {
  onNavigate: (screen: any) => void;
  onContinue: () => void;
}

type StepKey = 'datetime' | 'duration' | 'delivery' | 'extras' | 'review';
const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 'datetime', label: 'When' },
  { key: 'duration', label: 'How long' },
  { key: 'delivery', label: 'How' },
  { key: 'extras',   label: 'Extras' },
  { key: 'review',   label: 'Review' },
];

export default function BookingScreen({ onNavigate, onContinue }: BookingScreenProps) {
  const { data: machines } = useMachines();
  const { data: packages } = useRentalPackages();
  const { data: zones } = useDeliveryZones();
  const { data: pickupData } = usePickupLocations();
  const { data: addOnsData } = useAddOns();

  const machine = machines && machines.length > 0 ? machines[0] : null;
  const rentalPackages = packages || [];
  const dubaiLocations = zones || [];
  const pickupLocations = pickupData || [];
  const addOns = addOnsData || [];

  const {
    bookingData,
    setMachine: setStoreMachine,
    setPackage,
    setDateTime,
    setDeliveryType,
    setDeliveryAddress,
    setPickupLocation,
    addAddOn,
    removeAddOn,
    updateAddOnQuantity,
    setWaiveDeposit,
  } = useBookingStore();

  const { isVerified } = useIdentityVerification();

  // If the user is already verified, keep the booking store in sync so the
  // deposit is waived everywhere without an extra click.
  useEffect(() => {
    if (isVerified && !bookingData.waiveDeposit) setWaiveDeposit(true);
  }, [isVerified, bookingData.waiveDeposit, setWaiveDeposit]);

  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('14:00');
  // Default to the first (shortest) package; we sync to the
  // pre-selected package below once both are loaded.
  const [selectedDuration, setSelectedDuration] = useState(0);
  // Default to pickup so the sticky total matches the rate card until
  // the user explicitly chooses delivery in step 3.
  const [deliveryType, setDeliveryTypeLocal] = useState<'delivery' | 'pickup'>('pickup');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPickupId, setSelectedPickupId] = useState('');

  // Seed defaults
  useEffect(() => {
    if (machine && !bookingData.machine) setStoreMachine(machine);
    if (!selectedDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow.toISOString().split('T')[0]);
    }
    if (dubaiLocations.length > 0 && !selectedLocation) setSelectedLocation(dubaiLocations[0].id);
    if (pickupLocations.length > 0 && !selectedPickupId) setSelectedPickupId(pickupLocations[0].id);
  }, [machine, dubaiLocations, pickupLocations]);

  // Sync selectedDuration to whichever package was pre-selected (e.g. from
  // the Home "Book Now" buttons) once packages have loaded.
  useEffect(() => {
    if (!bookingData.package || rentalPackages.length === 0) return;
    const idx = rentalPackages.findIndex((p: any) => p.id === bookingData.package!.id);
    if (idx >= 0 && idx !== selectedDuration) setSelectedDuration(idx);
  }, [bookingData.package, rentalPackages]);

  // --- Dates ----------------------------------------------------------------
  const dates = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i);
        return date.toISOString().split('T')[0];
      }),
    [],
  );

  // --- Times ---------------------------------------------------------------
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let h = 8; h <= 20; h++) {
      slots.push(`${String(h).padStart(2, '0')}:00`);
      if (h < 20) slots.push(`${String(h).padStart(2, '0')}:30`);
    }
    return slots;
  }, []);

  const isToday = selectedDate
    ? new Date(selectedDate).toDateString() === new Date().toDateString()
    : false;

  const minTimeToday = useMemo(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }, [selectedDate]);

  const isSlotDisabled = (slot: string) => isToday && slot < minTimeToday;

  const groupedSlots = useMemo(() => {
    const morning: string[] = [];
    const afternoon: string[] = [];
    const evening: string[] = [];
    for (const s of timeSlots) {
      const h = parseInt(s.slice(0, 2), 10);
      if (h < 12) morning.push(s);
      else if (h < 17) afternoon.push(s);
      else evening.push(s);
    }
    return [
      { key: 'morning',   label: 'Morning',   icon: Sun,    hint: '8 AM – 12 PM',  slots: morning },
      { key: 'afternoon', label: 'Afternoon', icon: Sunset, hint: '12 PM – 5 PM',  slots: afternoon },
      { key: 'evening',   label: 'Evening',   icon: Moon,   hint: '5 PM – 8 PM',   slots: evening },
    ];
  }, [timeSlots]);

  const formatTimeLabel = (slot: string) => {
    const [h, m] = slot.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const [activePeriod, setActivePeriod] = useState<'morning' | 'afternoon' | 'evening'>('afternoon');
  useEffect(() => {
    if (!selectedTime) return;
    const h = parseInt(selectedTime.slice(0, 2), 10);
    setActivePeriod(h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening');
  }, [selectedTime]);

  useEffect(() => {
    if (isToday && selectedTime && selectedTime < minTimeToday) {
      const next = timeSlots.find((s) => s >= minTimeToday);
      if (next) setSelectedTime(next);
    }
  }, [isToday, minTimeToday, selectedTime, timeSlots]);

  // --- Pricing helpers ------------------------------------------------------
  const pkg = rentalPackages[selectedDuration];
  const deliveryFee = deliveryType === 'delivery'
    ? (dubaiLocations.find((z) => z.id === selectedLocation)?.delivery_fee ?? 50)
    : 0;
  const addOnsTotal = bookingData.addOns.reduce((sum, a) => sum + a.addOn.price * a.quantity, 0);
  // Security deposit: honour per-package value if set, otherwise fall back
  // to the AED 500 business default. Respect the store's waive flag
  // (e.g. after identity verification).
  const depositAmount = bookingData.waiveDeposit ? 0 : (pkg?.deposit_required || 500);
  const rentalSubtotal = (pkg?.price ?? 0) + addOnsTotal + deliveryFee;
  const dueNow = rentalSubtotal + depositAmount;

  // --- Add-ons helpers ------------------------------------------------------
  const toggleAddOn = (addOn: any, delta: number) => {
    const currentQty = bookingData.addOns.find((a) => a.addOn.id === addOn.id)?.quantity || 0;
    const newQty = Math.max(0, currentQty + delta);
    if (newQty === 0) removeAddOn(addOn.id);
    else if (currentQty === 0) addAddOn(addOn, newQty);
    else updateAddOnQuantity(addOn.id, newQty);
  };

  // --- Step navigation ------------------------------------------------------
  const canAdvance: Record<StepKey, boolean> = {
    datetime: !!selectedDate && !!selectedTime,
    duration: !!pkg,
    delivery:
      (deliveryType === 'pickup' && !!selectedPickupId) ||
      (deliveryType === 'delivery' && !!selectedLocation),
    extras: true,
    review: true,
  };

  const next = () => {
    if (!canAdvance[step.key]) return;
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      commitAndContinue();
    }
  };

  const back = () => {
    if (stepIndex === 0) onNavigate('product');
    else setStepIndex(stepIndex - 1);
  };

  const commitAndContinue = () => {
    if (!machine || !pkg) return;
    const startDateTime = new Date(`${selectedDate}T${selectedTime}`);
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(endDateTime.getHours() + pkg.duration_hours);
    const endDate = endDateTime.toISOString().split('T')[0];
    const endTime = endDateTime.toTimeString().substring(0, 5);

    setPackage(pkg);
    setDateTime(selectedDate, selectedTime, endDate, endTime);
    setDeliveryType(deliveryType);
    if (deliveryType === 'delivery' && selectedLocation) {
      const zone = dubaiLocations.find((z) => z.id === selectedLocation);
      if (zone) setDeliveryAddress(selectedLocation, zone.name, zone);
    }
    if (deliveryType === 'pickup' && selectedPickupId) {
      setPickupLocation(selectedPickupId);
    }
    onContinue();
  };

  // --- Render ---------------------------------------------------------------
  const progressPct = ((stepIndex + 1) / STEPS.length) * 100;
  const titles: Record<StepKey, { title: string; subtitle: string }> = {
    datetime: { title: 'When do you want it?', subtitle: 'Pick a date and start time' },
    duration: { title: 'How long?',              subtitle: 'Choose your rental package' },
    delivery: { title: 'Delivery or pickup?',    subtitle: 'We bring it to you, or meet us at the hub' },
    extras:   { title: 'Add anything extra?',    subtitle: 'Optional — skip if you don\'t need them' },
    review:   { title: 'Review & continue',       subtitle: 'Confirm your booking details' },
  };

  return (
    <div className="w-full min-h-screen bg-background flex flex-col pb-28">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={back}
            className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Book PT-9001</h1>
            <p className="text-xs text-muted-foreground">
              Step {stepIndex + 1} of {STEPS.length} · {step.label}
            </p>
          </div>
        </div>
        <div className="h-1 bg-secondary">
          <div
            className="h-1 bg-[#1A6B4F] transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step body */}
      <div className="flex-1 relative overflow-x-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="px-6 py-5 space-y-5"
          >
            <div>
              <h2 className="text-2xl">{titles[step.key].title}</h2>
              <p className="text-sm text-muted-foreground mt-1">{titles[step.key].subtitle}</p>
            </div>

            {/* ---- Step 1: Date & Time ---- */}
            {step.key === 'datetime' && (
              <>
                <SectionLabel icon={Calendar}>Date</SectionLabel>
                <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar snap-x">
                  {dates.map((date) => {
                    const d = new Date(date);
                    const sel = selectedDate === date;
                    const today = d.toDateString() === new Date().toDateString();
                    return (
                      <button
                        key={date}
                        onClick={() => setSelectedDate(date)}
                        className={`flex-shrink-0 snap-start w-16 h-20 rounded-2xl flex flex-col items-center justify-center transition-all ${
                          sel
                            ? 'bg-[#1A6B4F] text-white shadow-sm'
                            : 'bg-card border border-border hover:border-[#1A6B4F]/40'
                        }`}
                      >
                        <div className={`text-[11px] uppercase tracking-wide ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                          {today ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="text-xl font-medium mt-0.5 leading-none">{d.getDate()}</div>
                        <div className={`text-[11px] mt-1 ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                          {d.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-2">
                  <SectionLabel icon={Clock}>Time</SectionLabel>
                  {selectedTime && (
                    <span className="text-sm text-[#1A6B4F] font-medium">
                      {formatTimeLabel(selectedTime)}
                    </span>
                  )}
                </div>

                <div className="flex gap-1 p-1 bg-secondary rounded-2xl">
                  {groupedSlots.map(({ key, label, icon: Icon, slots }) => {
                    const allDisabled = slots.every(isSlotDisabled);
                    const active = activePeriod === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setActivePeriod(key as typeof activePeriod)}
                        disabled={allDisabled}
                        className={`flex-1 h-10 rounded-xl text-sm flex items-center justify-center gap-1.5 transition-all ${
                          active
                            ? 'bg-background shadow-sm text-[#1A6B4F]'
                            : allDisabled
                              ? 'text-muted-foreground/60 cursor-not-allowed'
                              : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {(groupedSlots.find((g) => g.key === activePeriod)?.slots ?? []).map((time) => {
                    const disabled = isSlotDisabled(time);
                    const sel = selectedTime === time;
                    return (
                      <button
                        key={time}
                        onClick={() => !disabled && setSelectedTime(time)}
                        disabled={disabled}
                        className={`h-11 rounded-xl text-sm transition-all ${
                          disabled
                            ? 'bg-secondary/50 text-muted-foreground/50 line-through cursor-not-allowed'
                            : sel
                              ? 'bg-[#1A6B4F] text-white shadow-sm'
                              : 'bg-card border border-border hover:border-[#1A6B4F]/40 hover:bg-[#D4F04A]/10'
                        }`}
                      >
                        {formatTimeLabel(time)}
                      </button>
                    );
                  })}
                </div>

                {isToday && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    Same-day bookings need at least 1 hour lead time.
                  </p>
                )}
              </>
            )}

            {/* ---- Step 2: Duration ---- */}
            {step.key === 'duration' && (
              <div className="space-y-2">
                {rentalPackages.slice(0, 5).map((p: any, index: number) => {
                  const sel = selectedDuration === index;
                  const perHour = (p.price / p.duration_hours).toFixed(0);
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedDuration(index)}
                      className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
                        sel
                          ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                          : 'border-border bg-card hover:border-[#1A6B4F]/40'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex gap-3 items-start flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            sel ? 'bg-[#1A6B4F] text-white' : 'bg-secondary text-[#1A6B4F]'
                          }`}>
                            <Package className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{p.name}</span>
                              {p.is_popular && (
                                <span className="px-2 py-0.5 bg-[#D4F04A] text-[#1a1a1a] text-[11px] rounded-full">
                                  Popular
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {p.duration_hours} hours · AED {perHour}/hr
                            </div>
                          </div>
                        </div>
                        <span className="text-lg text-[#1A6B4F] font-medium">AED {p.price}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ---- Step 3: Delivery ---- */}
            {step.key === 'delivery' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <DeliveryOption
                    selected={deliveryType === 'delivery'}
                    onClick={() => setDeliveryTypeLocal('delivery')}
                    icon={Truck}
                    title="Delivery"
                    subtitle={`+ AED ${deliveryFee}`}
                  />
                  <DeliveryOption
                    selected={deliveryType === 'pickup'}
                    onClick={() => setDeliveryTypeLocal('pickup')}
                    icon={Store}
                    title="Self pickup"
                    subtitle="Free"
                  />
                </div>

                {deliveryType === 'delivery' && (
                  <div>
                    <SectionLabel icon={MapPin}>Delivery area</SectionLabel>
                    <div className="grid gap-2">
                      {dubaiLocations.map((loc: any) => {
                        const sel = selectedLocation === loc.id;
                        return (
                          <button
                            key={loc.id}
                            onClick={() => setSelectedLocation(loc.id)}
                            className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center justify-between ${
                              sel
                                ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                                : 'border-border bg-card hover:border-[#1A6B4F]/40'
                            }`}
                          >
                            <div>
                              <div className="text-sm font-medium">{loc.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {loc.estimated_delivery_time_minutes ? `~${loc.estimated_delivery_time_minutes} min delivery` : 'Delivery fee varies'}
                              </div>
                            </div>
                            <span className="text-sm text-[#1A6B4F]">AED {loc.delivery_fee}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {deliveryType === 'pickup' && (
                  <div>
                    <SectionLabel icon={MapPin}>Pickup location</SectionLabel>
                    {pickupLocations.length === 0 ? (
                      <div className="text-center text-sm text-muted-foreground py-6 bg-card border border-border rounded-2xl">
                        No pickup hubs available right now — please choose delivery.
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        {pickupLocations.map((loc: any) => {
                          const sel = selectedPickupId === loc.id;
                          return (
                            <button
                              key={loc.id}
                              onClick={() => setSelectedPickupId(loc.id)}
                              className={`p-4 rounded-2xl border-2 transition-all text-left ${
                                sel
                                  ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                                  : 'border-border bg-card hover:border-[#1A6B4F]/40'
                              }`}
                            >
                              <div className="text-sm font-medium">{loc.name}</div>
                              {loc.address && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {loc.address}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ---- Step 4: Extras ---- */}
            {step.key === 'extras' && (
              <div className="space-y-3">
                {addOns.length > 0 ? (
                  addOns.map((addOn: any) => {
                    const quantity = bookingData.addOns.find((a) => a.addOn.id === addOn.id)?.quantity || 0;
                    return (
                      <div key={addOn.id} className="bg-card border border-border rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl">{addOn.icon || '📦'}</span>
                              <span className="text-sm font-medium">{addOn.name}</span>
                            </div>
                            {addOn.description && (
                              <p className="text-xs text-muted-foreground">{addOn.description}</p>
                            )}
                            <div className="text-sm text-[#1A6B4F] mt-1">AED {addOn.price}</div>
                          </div>
                          {quantity === 0 ? (
                            <button
                              onClick={() => toggleAddOn(addOn, 1)}
                              className="h-9 px-4 rounded-lg bg-[#D4F04A] hover:bg-[#b5e605] text-[#1a1a1a] text-sm"
                            >
                              Add
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleAddOn(addOn, -1)}
                                className="w-9 h-9 rounded-lg bg-secondary hover:bg-muted flex items-center justify-center"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-6 text-center text-sm font-medium">{quantity}</span>
                              <button
                                onClick={() => toggleAddOn(addOn, 1)}
                                className="w-9 h-9 rounded-lg bg-secondary hover:bg-muted flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No add-ons available.
                  </div>
                )}
              </div>
            )}

            {/* ---- Step 5: Review ---- */}
            {step.key === 'review' && pkg && (
              <div className="space-y-3">
                <ReviewRow
                  label="When"
                  value={`${new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${formatTimeLabel(selectedTime)}`}
                  onEdit={() => setStepIndex(0)}
                />
                <ReviewRow
                  label="Duration"
                  value={`${pkg.name} · ${pkg.duration_hours} hours`}
                  onEdit={() => setStepIndex(1)}
                />
                <ReviewRow
                  label={deliveryType === 'delivery' ? 'Delivery' : 'Pickup'}
                  value={
                    deliveryType === 'delivery'
                      ? dubaiLocations.find((z) => z.id === selectedLocation)?.name ?? '—'
                      : pickupLocations.find((l: any) => l.id === selectedPickupId)?.name ?? 'Collect from hub'
                  }
                  onEdit={() => setStepIndex(2)}
                />
                {bookingData.addOns.length > 0 && (
                  <ReviewRow
                    label="Extras"
                    value={bookingData.addOns.map((a) => `${a.addOn.name} × ${a.quantity}`).join(', ')}
                    onEdit={() => setStepIndex(3)}
                  />
                )}

                <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-5 text-white">
                  <div className="space-y-2 text-sm">
                    <Line label={`${pkg.name} Rental`} value={`AED ${pkg.price}`} />
                    {addOnsTotal > 0 && <Line label="Add-ons" value={`AED ${addOnsTotal}`} />}
                    {deliveryFee > 0 && <Line label="Delivery fee" value={`AED ${deliveryFee}`} />}

                    <div className="h-px bg-white/20 my-2" />

                    {bookingData.waiveDeposit ? (
                      <div className="flex justify-between">
                        <span className="opacity-80">Security deposit</span>
                        <span className="flex items-center gap-2">
                          <span className="opacity-60 line-through">AED {pkg.deposit_required || 500}</span>
                          <span className="text-[#D4F04A]">Waived</span>
                        </span>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between">
                          <span className="opacity-80">Security deposit</span>
                          <span>AED {depositAmount}</span>
                        </div>
                        <div className="text-[11px] opacity-60 mt-0.5">
                          Refunded after you return the machine in good condition.
                        </div>
                      </div>
                    )}

                    <div className="h-px bg-white/20 my-2" />
                    <div className="flex justify-between text-base">
                      <span>Total due now</span>
                      <span className="text-[#D4F04A] font-medium">AED {dueNow.toFixed(0)}</span>
                    </div>
                  </div>
                </div>

                {!bookingData.waiveDeposit && depositAmount > 0 && (
                  <button
                    type="button"
                    onClick={() => onNavigate('identity-verification')}
                    className="w-full text-left bg-[#D4F04A]/15 border border-[#1A6B4F]/25 rounded-2xl p-4 flex items-center gap-3 hover:bg-[#D4F04A]/25 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#1A6B4F] flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1A6B4F]">
                        Skip the AED {depositAmount} deposit
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Verify your ID once — no deposit on this or any future booking.
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#1A6B4F] flex-shrink-0" />
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Sticky bottom nav — sits above the 20 (80px) bottom tab bar */}
      <div className="fixed bottom-20 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3">
        <div className="flex items-center gap-3">
          {stepIndex > 0 && (
            <button
              onClick={back}
              className="h-12 px-5 rounded-xl border border-border hover:bg-secondary flex items-center gap-1 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button
            onClick={next}
            disabled={!canAdvance[step.key]}
            className="flex-1 h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {step.key === 'review' ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Continue to Checkout
              </>
            ) : step.key === 'extras' && bookingData.addOns.length === 0 ? (
              <>Skip</>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Small helpers -----------------------------------------------------------
function SectionLabel({ icon: Icon, children }: { icon: any; children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-medium flex items-center gap-2 mb-2 mt-4 first:mt-0">
      <Icon className="w-4 h-4 text-[#1A6B4F]" />
      {children}
    </h3>
  );
}

function DeliveryOption({
  selected, onClick, icon: Icon, title, subtitle,
}: {
  selected: boolean; onClick: () => void; icon: any; title: string; subtitle: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`h-28 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-1 ${
        selected ? 'border-[#1A6B4F] bg-[#D4F04A]/10' : 'border-border bg-card hover:border-[#1A6B4F]/40'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-1 ${
        selected ? 'bg-[#1A6B4F] text-white' : 'bg-secondary text-[#1A6B4F]'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
    </button>
  );
}

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
      <button
        onClick={onEdit}
        className="text-xs text-[#1A6B4F] hover:underline flex-shrink-0"
      >
        Edit
      </button>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="opacity-80">{label}</span>
      <span>{value}</span>
    </div>
  );
}
